import { Typography } from "@mui/material";
import { StyledComponentProps } from "@mui/styles";
import { FeedClassKey, GlobalClassKeys } from "assets/jss/common-styles";
import classNames from "classnames";
import IconTrophy from "components/Icons/IconTrophy";
import { JoinComponents } from "components/JoinComponents";
import MuiClickable from "components/MuiExtras/MuiClickable";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiLink from "components/MuiExtras/MuiLink";
import MuiColor from "components/MuiExtras/Typography/MuiColor";
import MuiReverse from "components/MuiExtras/Typography/MuiReverse";
import MuiWarning from "components/MuiExtras/Typography/MuiWarning";
import { Formaters } from "lib/Formarters";
import { FeedType, PublicationType } from "models/ApiEnums";
import { FeedItemDto } from "models/ApiModels";
import moment from "moment";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";

/////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    data: DeepPartial<FeedItemDto>;
    feedType: FeedType;
    fullView: boolean;
    onTitleClick?: () => void;
    onAnswerDetailsClick?: () => void;
}

type Props = IProps & StyledComponentProps<FeedClassKey> & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

class TitleLine extends React.PureComponent<Props> {

    ///
    private getCourseSubtitle() {

        const {
            t,
            data: {
                publication: {
                    course: {
                        viewPeriodEnd,
                        gainPoints
                    }
                },
                interaction: {
                    courseEnroll: {
                        expired,
                        completedOn,
                        gainedPoints,
                        progress
                    }
                }
            }
        } = this.props;

        const subtitle: React.ReactNode[] = [];
        if ((completedOn != null && gainedPoints != null) || (completedOn == null && gainPoints > 0 && !expired)) {
            subtitle.push(
                <MuiColor variant={completedOn != null ? "success" : "muted"}>
                    <IconTrophy />
                    <span>&nbsp;</span>
                    <span>
                        {
                            completedOn != null
                                ? t("Common.Feed.Course.WonPointMessage", { count: gainedPoints })
                                : t("Common.Feed.Course.WillGainPointMessage", { count: gainPoints })
                        }
                    </span>
                </MuiColor>
            );
        }

        if (this.props.feedType == FeedType.CourseViewer || this.props.feedType == FeedType.CourseEnrolled || this.props.feedType == FeedType.CourseCompleted) {
            if (completedOn != null) {
                if (progress !== 100)
                    subtitle.push(<MuiColor variant="error">{t("Common.Feed.Course.ExpiredOn", { completedOn: completedOn })}</MuiColor>);
                else
                    subtitle.push(<MuiColor variant="info">{t("Common.Feed.Course.CompletedOn", { completedOn: completedOn })}</MuiColor>);
            }
            else if (expired === true) {
                subtitle.push(<MuiColor variant="error">{t("Common.Feed.Course.NotCompletedOnTime")}</MuiColor>);
            }
            else if (viewPeriodEnd != null) {
                var dueDate = moment(viewPeriodEnd);
                var diffDays = dueDate.diff(moment(), "days");
                if (diffDays > 0 && diffDays < 8) {
                    subtitle.push(<MuiColor variant="info">{t("Common.Feed.Course.WillExpireDaysText", { count: diffDays })}</MuiColor>);
                }
                else if (diffDays == 0) {
                    subtitle.push(<MuiColor variant="warning">{t("Common.Feed.Course.WillExpiresTodayText")}</MuiColor>);
                }
                else if (diffDays < 0) {
                    subtitle.push(<MuiColor variant="error">{t("Common.Feed.Course.ExpiredText")}</MuiColor>);
                }
                else {
                    subtitle.push(<MuiColor variant="info">{t("Common.Feed.Course.StayAvailableUntilText", { untilDate: viewPeriodEnd })}</MuiColor>);
                }
            }
        }

        return subtitle;
    }

    ///
    private renderTitleContent() {
        const {
            data: {
                publication: { title, blocked },
            },
        } = this.props;

        const titleBlock = blocked ? <MuiWarning>{title}</MuiWarning> : title;

        return (
            <Typography variant="h5">
                <span>{titleBlock}</span>
            </Typography>
        );
    }

    ///
    private renderSubtitleContent() {
        if (this.props.data.publicationType != PublicationType.Course)
            return null;

        const subtitle = this.getCourseSubtitle();
        return subtitle.length == null
            ? null
            : (
                <div>
                    <Typography variant="subtitle2" color="inherit">
                        <JoinComponents components={subtitle} separator="&nbsp;&bull;&nbsp;" />
                    </Typography>
                </div>
            );
    }

    ///
    render() {
        const {
            t,
            classes,
            data: {
                publicationType,
                interaction,
                statistics,
                permissions,
                bestAnswer,
                body
            },
            fullView,
            onTitleClick,
            onAnswerDetailsClick
        } = this.props;


        const cls =
            classNames(
                classes.titleContainer,
                {
                    [classes.newItemLeftMark]: interaction && interaction.callout === true
                });

        const titleClick = permissions.hasAccess && (interaction == null || interaction.courseItem == null || (!interaction.courseItem.containVideo && !interaction.courseItem.containEmbedVideo))
            ? onTitleClick
            : null;

        const answerDetailsClick = permissions.hasAccess
            ? onAnswerDetailsClick
            : null;

        if (publicationType == PublicationType.Question) {
            return (
                <div className={GlobalClassKeys.clearfix}>
                    <MuiContainer margin="dense" className={cls}>

                        <MuiLink onClick={titleClick} className="aCardTitle">
                            {this.renderTitleContent()}
                        </MuiLink>

                        {
                            !fullView && statistics && statistics.answers > 1 &&
                            <div className={classes.titleHighlight}>
                                <MuiClickable onClick={answerDetailsClick || titleClick}>
                                    <MuiReverse color="warning">{t("Common.Feed.Question.MoreAnswersText", { count: statistics.answers })}</MuiReverse>
                                </MuiClickable>
                            </div>
                        }

                        {
                            !fullView && bestAnswer == null && body && (body.summary != null || body.attachs.length > 0) &&
                            <div className={classes.titleHighlight}>
                                <MuiReverse color="warning">{t("Common.Feed.Question.MoreDetailsText")}</MuiReverse>
                            </div>
                        }
                    </MuiContainer>
                </div>
            );
        }

        return (
            <MuiContainer margin="dense" className={cls}>
                <MuiLink onClick={titleClick} className="aCardTitle" >
                    {this.renderTitleContent()}
                </MuiLink>
                {this.renderSubtitleContent()}
            </MuiContainer>
        );
    }
}

export default withTranslation()(TitleLine);